import { useEffect, useRef, useState } from 'react';
import type { AnnotationMap, Stroke, StrokePoint, StrokesBlock } from '../types/models';
import { A4_LOGICAL_WIDTH, PAGE_MIN_HEIGHT } from '../utils/answers';
import { ANNOTATION_COLOR, drawStroke, sheetTypeOf, strokesMaxY } from '../utils/inkRender';

// Crop a page to its content when displaying: tight for read-only, with more room to write
// corrections while editing. Padding/min are logical px in the A4 space.
const READONLY_PAD = 40, READONLY_MIN = 96;
const EDIT_PAD = 130, EDIT_MIN = 260;

// Staff correct answers in one firm red; the pen never changes colour (per requirement).
const PEN_WIDTH = 2.5;
const ERASER_RADIUS = 16; // logical px — stroke-level erase of the staff's own marks

const BACKING_SCALE = Math.min(window.devicePixelRatio || 1, 3);

/**
 * One answer page with a red-pen annotation layer. The student's ink is drawn read-only underneath
 * (the ruled/graph paper comes from CSS, matching the exam editor); staff strokes are drawn on top
 * in red. Pointer input only ever adds/removes the red annotation strokes — the student's original
 * answer is never modified.
 */
function AnnotationCanvas({ page, strokes, readOnly, tool, onChange }: {
  page: StrokesBlock;
  strokes: Stroke[];
  readOnly?: boolean;
  tool: 'pen' | 'eraser';
  onChange: (next: Stroke[]) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const drawingRef = useRef<Stroke | null>(null);
  const cursorRef = useRef<{ x: number; y: number } | null>(null);

  const sheetType = sheetTypeOf(page);
  const logicalWidth = page.width || A4_LOGICAL_WIDTH;
  // Crop the visible page to its content so blank space below the writing doesn't clutter review.
  const fullHeight = Math.max(PAGE_MIN_HEIGHT, page.height || PAGE_MIN_HEIGHT);
  const maxY = strokesMaxY(page.strokes, strokes);
  const pad = readOnly ? READONLY_PAD : EDIT_PAD;
  const min = readOnly ? READONLY_MIN : EDIT_MIN;
  const heightLogical = maxY > 0 ? Math.min(fullHeight, Math.max(min, Math.ceil(maxY + pad))) : min;

  useEffect(() => { redraw(); });

  function redraw() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const k = canvas.width / logicalWidth;
    ctx.setTransform(k, 0, 0, k, 0, 0);
    for (const s of page.strokes) drawStroke(ctx, s);          // student ink (read-only)
    for (const s of strokes) drawStroke(ctx, s);               // staff corrections (red)
    if (drawingRef.current) drawStroke(ctx, drawingRef.current);
    if (tool === 'eraser' && cursorRef.current && !readOnly) {
      ctx.save();
      ctx.strokeStyle = ANNOTATION_COLOR;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([5, 4]);
      ctx.beginPath();
      ctx.arc(cursorRef.current.x, cursorRef.current.y, ERASER_RADIUS, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
  }

  useEffect(() => {
    const canvas = canvasRef.current, wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ro = new ResizeObserver(() => {
      const rect = wrap.getBoundingClientRect();
      const w = rect.width || logicalWidth;
      const h = rect.height || w * (heightLogical / logicalWidth);
      canvas.width = Math.max(1, w * BACKING_SCALE);
      canvas.height = Math.max(1, h * BACKING_SCALE);
      redraw();
    });
    ro.observe(wrap);
    return () => ro.disconnect();
  }, []);

  function pointFromEvent(e: React.PointerEvent): StrokePoint {
    const rect = canvasRef.current!.getBoundingClientRect();
    const k = logicalWidth / (rect.width || logicalWidth);
    return {
      x: (e.clientX - rect.left) * k,
      y: (e.clientY - rect.top) * k,
      p: e.pressure && e.pressure > 0 ? e.pressure : 0.5,
      t: 0,
    };
  }

  // Remove any annotation stroke with a point inside the eraser circle (stroke-level erase).
  function eraseAt(pt: StrokePoint): boolean {
    const r2 = ERASER_RADIUS * ERASER_RADIUS;
    const next = strokes.filter(s =>
      !s.points.some(p => (p.x - pt.x) ** 2 + (p.y - pt.y) ** 2 <= r2));
    if (next.length === strokes.length) return false;
    onChange(next);
    return true;
  }

  function handleDown(e: React.PointerEvent) {
    if (readOnly) return;
    e.preventDefault();
    (e.target as Element).setPointerCapture(e.pointerId);
    const pt = pointFromEvent(e);
    if (tool === 'eraser') {
      cursorRef.current = { x: pt.x, y: pt.y };
      if (!eraseAt(pt)) redraw();
      return;
    }
    drawingRef.current = {
      id: crypto.randomUUID(),
      color: ANNOTATION_COLOR,
      width: PEN_WIDTH,
      pointerType: (e.pointerType as 'pen' | 'touch' | 'mouse') || 'mouse',
      points: [pt],
      pen: 'gel',
    };
  }

  function handleMove(e: React.PointerEvent) {
    if (readOnly) return;
    if (tool === 'eraser') {
      const pt = pointFromEvent(e);
      cursorRef.current = { x: pt.x, y: pt.y };
      if (!((e.buttons & 1) && eraseAt(pt))) redraw();
      return;
    }
    const cur = drawingRef.current;
    if (!cur) return;
    const events = (e.nativeEvent as PointerEvent).getCoalescedEvents?.() ?? [e.nativeEvent as PointerEvent];
    const rect = canvasRef.current!.getBoundingClientRect();
    const k = logicalWidth / (rect.width || logicalWidth);
    for (const pe of events) {
      cur.points.push({
        x: (pe.clientX - rect.left) * k,
        y: (pe.clientY - rect.top) * k,
        p: pe.pressure && pe.pressure > 0 ? pe.pressure : 0.5,
        t: 0,
      });
    }
    redraw();
  }

  function handleUp() {
    const cur = drawingRef.current;
    drawingRef.current = null;
    if (!cur || cur.points.length < 2) { redraw(); return; }
    onChange([...strokes, cur]);
  }

  function handleLeave() {
    handleUp();
    if (cursorRef.current) { cursorRef.current = null; redraw(); }
  }

  return (
    <div ref={wrapRef} className={`ink-canvas-wrap sheet-${sheetType}`}
         style={{ aspectRatio: `${logicalWidth} / ${heightLogical}`, touchAction: readOnly ? 'auto' : 'none' }}>
      <canvas
        ref={canvasRef}
        className="ink-canvas"
        style={{ cursor: readOnly ? 'default' : 'crosshair', touchAction: readOnly ? 'auto' : 'none' }}
        onPointerDown={handleDown}
        onPointerMove={handleMove}
        onPointerUp={handleUp}
        onPointerCancel={handleUp}
        onPointerLeave={handleLeave}
      />
    </div>
  );
}

/**
 * A multi-page answer with a red-pen correction layer. In edit mode (staff review) it shows a small
 * red-only toolbar (pen / erase / undo); read-only mode (student result, print) just overlays the
 * saved corrections. Annotations are keyed by the student's page id so single- and multi-part
 * answers both work — every ink page has a unique id.
 */
export function AnnotationPad({ pages, annotations, onChange, readOnly }: {
  pages: StrokesBlock[];
  annotations: AnnotationMap;
  onChange?: (next: AnnotationMap) => void;
  readOnly?: boolean;
}) {
  const [tool, setTool] = useState<'pen' | 'eraser'>('pen');

  const setPageStrokes = (pageId: string, next: Stroke[]) =>
    onChange?.({ ...annotations, [pageId]: next });

  // Only show pages that actually hold ink (student or staff) — a blank page is just noise.
  const visiblePages = pages.filter(p => strokesMaxY(p.strokes, annotations[p.id]) > 0);

  // Undo the most recent staff stroke across all pages.
  function undo() {
    if (!onChange) return;
    for (let i = pages.length - 1; i >= 0; i--) {
      const id = pages[i].id;
      const s = annotations[id];
      if (s && s.length > 0) {
        onChange({ ...annotations, [id]: s.slice(0, -1) });
        return;
      }
    }
  }
  const hasMarks = pages.some(p => (annotations[p.id]?.length ?? 0) > 0);

  return (
    <div className="ink-pad ink-pad--readonly">
      {!readOnly && (
        <div className="ink-toolbar">
          <span className="muted">Correct in red</span>
          <button type="button" onClick={() => setTool('pen')} aria-pressed={tool === 'pen'}
                  style={{ background: tool === 'pen' ? 'var(--accent-soft-bg)' : undefined }}
                  title="Red pen">✎ Red pen</button>
          <button type="button" onClick={() => setTool('eraser')} aria-pressed={tool === 'eraser'}
                  style={{ background: tool === 'eraser' ? 'var(--danger-soft-bg)' : undefined }}
                  title="Erase your corrections (student ink is never erased)">Erase</button>
          <button type="button" onClick={undo} disabled={!hasMarks}>Undo</button>
          <span className="muted" style={{ marginLeft: 'auto', color: ANNOTATION_COLOR }}>● correcting in red</span>
        </div>
      )}
      <div className="ink-pages">
        {visiblePages.map((p, i) => (
          <div className="ink-page" key={p.id}>
            <div className="ink-page-header">
              <span>Page {i + 1} of {visiblePages.length}</span>
            </div>
            <AnnotationCanvas
              page={p}
              strokes={annotations[p.id] ?? []}
              readOnly={readOnly}
              tool={tool}
              onChange={next => setPageStrokes(p.id, next)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
